import type { MediaStatus, MediaVariantDto } from '@/common/media';

export type UUID = string;
export type ISODateString = string;

export enum TaskTypes {
  TIME_BASED = 'time_based',
  DEADLINE_BASED = 'deadline_based',
}

export const TASK_TYPES = [
  { id: TaskTypes.TIME_BASED, label: 'Time Based' },
  { id: TaskTypes.DEADLINE_BASED, label: 'Deadline Based' },
] as const;

export enum TaskStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  ON_HOLD = 'on_hold',
  ON_APPROVAL = 'on_approval',
  COMPLETED = 'completed',
  CANCELED = 'canceled',
  DENIED = 'denied',
  FAILED = 'failed',
}

export const TASK_STATUSES = [
  { id: TaskStatus.PENDING, label: 'Pending' },
  { id: TaskStatus.IN_PROGRESS, label: 'In Progress' },
  { id: TaskStatus.ON_HOLD, label: 'On Hold' },
  { id: TaskStatus.ON_APPROVAL, label: 'On Approval' },
  { id: TaskStatus.COMPLETED, label: 'Completed' },
  { id: TaskStatus.CANCELED, label: 'Canceled' },
  { id: TaskStatus.FAILED, label: 'Failed' },
] as const;

export type TaskStatusType = TaskStatus;

/**
 * The two-state list the maintenance surfaces run on: a job is either still to do or
 * done. In Progress and On Review describe delegated task workflow, which a schedule
 * does not model — the schedule's own filters are Overdue / Due soon / Upcoming / Done.
 *
 * Service requests run on it too: a request is raised or it is served, and the states
 * in between belong to the provider's own view, which keeps SP_ALLOWED_STATUSES.
 */
export const TODO_DONE_STATUSES: readonly TaskStatus[] = [TaskStatus.PENDING, TaskStatus.COMPLETED];

export enum TaskPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
}

/**
 * Weakest first — the order the Priority column sorts by.
 *
 * `priority` is required on both create and update, and the service validates it as
 * `oneof=low medium high`, so these three are the whole vocabulary: a task cannot be
 * saved without one.
 */
export const TASK_PRIORITIES = [
  { id: TaskPriority.LOW, label: 'Low' },
  { id: TaskPriority.MEDIUM, label: 'Medium' },
  { id: TaskPriority.HIGH, label: 'High' },
] as const;

/**
 * The columns activity's `GET /tasks` can order by. `due` is the time slot's end;
 * `equipment` and `responsible` are the names stored on the task, not the live ones;
 * `cost` is `tasks.cost` alone. Nulls come last either way. `priority` and `status`
 * follow their enum's declaration order. Ties fall back to time slot start, then id.
 */
export type TasksSortField =
  | 'title'
  | 'due'
  | 'priority'
  | 'status'
  | 'equipment'
  | 'responsible'
  | 'cost';

export type TasksFilter = {
  limit: number;
  page: number;
  type?: TaskTypes;
  status?: TaskStatusType[];
  priority?: 'low' | 'medium' | 'high';
  from?: string;
  to?: string;
  starts_from?: string;
  starts_to?: string;
  ends_from?: string;
  ends_to?: string;
  /** Deadline (time slot end) at or after this moment — independent of `from`/`to`. */
  due_from?: string;
  /** Deadline (time slot end) before this moment. */
  due_to?: string;
  supervisor?: string;
  responsible?: string;
  equipment_id?: string;
  include_service_tasks?: boolean;
  participant_status?: string[];
  /** Left out, the service's default order: time slot start ascending. */
  sort?: TasksSortField;
  order?: 'asc' | 'desc';
};

export type TaskCalendar = {
  id: UUID;
  name: string;
};

export type TaskLocation = {
  id: UUID;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  map_url: string;
};

export type TaskTimeSlot = {
  id: UUID;
  type: 'day_based' | string;
  start: ISODateString;
  end: ISODateString;
  all_day?: boolean;
};

export enum TaskParticipantType {
  GUEST = 'guest',
  CREW = 'crew',
  SERVICE_PROVIDER = 'service_provider',
}

export type TaskParticipant = {
  person_id: UUID;
  type: TaskParticipantType;
  status?: any;
  name?: string;
};

/**
 * One step of a task's checklist. Activity keeps each step as its own row and answers them in
 * order. A task create takes its first steps; after that they change only through the
 * `/tasks/{id}/checklist` endpoints (`syncTaskChecklist`), since `PUT /tasks` ignores them.
 */
export type ChecklistItem = {
  /** The service's; a step not saved yet has none. */
  id?: string;
  status: 'open' | 'done';
  /** Up to `CHECKLIST_TEXT_MAX` characters — the service's own `max=200`. */
  text: string;
};

export const CHECKLIST_TEXT_MAX = 200;
/** The product's cap; the service takes any length. */
export const CHECKLIST_ITEMS_MAX = 50;

/** A file attached to a task — activity answers with this shape after an upload. */
export type TaskDocument = {
  id: UUID;
  name: string;
  link: string;
  content_type: string;
  size: number;
  /** Video and audio only — see {@link MediaStatus}. */
  media_status?: MediaStatus;
  variants?: Record<string, MediaVariantDto>;
};

/** Activity caps a task's documents at 10 — the 11th is a 400, not a swap. */
export const TASK_DOCUMENTS_MAX = 10;

export type TaskResponse = {
  id: UUID;
  generator_id: UUID | null;
  calendar: TaskCalendar;
  participants: TaskParticipant[];
  title: string;
  description: string;
  location: TaskLocation;
  created_at: ISODateString;
  updated_at: ISODateString;
  type: TaskTypes;
  status: TaskStatusType;
  priority: 'high' | string;
  start: ISODateString;
  deadline: ISODateString;
  reminder: ISODateString;
  time_slot: TaskTimeSlot;
  responsible_id: UUID;
  responsible: TaskParticipant | string | null;
  comments_count: number;
  /** Estimated cost of the job, in the vessel's currency. Null until someone prices it. */
  cost?: number | null;
  isOutOfWater?: boolean;
  equipment?: {
    id: string;
    name: string;
  } | null;
  checklist: ChecklistItem[];
  /** Up to `TASK_DOCUMENTS_MAX` per task — the PATCH appends rather than replaces. */
  documents?: TaskDocument[] | null;
  visibility: 'private' | 'public';
  event_id?: string;
  is_ai_generated: boolean;
  has_menu?: boolean;
};

export type TasksListResponse = {
  items: TaskResponse[];
  count: number;
};

export type TaskStatusUpdatePayload = {
  id: string;
  serviceId: string;
  status: TaskStatus;
};
